import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useConvexAuth } from 'convex/react';
import { api } from '@convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { InviteAuthForm } from '@/components/auth/InviteAuthForm';
import type { IInviteAuthFormWrapperProps } from '@/@types/organisation';

function InviteAuthFormWrapper({ email, token, orgName, onAccepted }: IInviteAuthFormWrapperProps) {
  const emailExists = useQuery(api.organisations.invitations.checkEmailExists, { email });

  if (emailExists === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <InviteAuthForm
        email={email}
        token={token}
        orgName={orgName}
        initialMode={emailExists ? 'signIn' : 'signUp'}
        onAccepted={onAccepted}
      />
    </div>
  );
}

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();

  const invitation = useQuery(api.organisations.invitations.getByToken, token ? { token } : 'skip');

  const acceptInvitation = useMutation(api.organisations.invitations.acceptInvitation);
  const declineInvitation = useMutation(api.organisations.invitations.declineInvitation);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Loading
  if (invitation === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  // Invalid token
  if (!invitation || !token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <XCircle aria-hidden="true" className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Invitation</h1>
          <p className="text-muted-foreground mb-6">
            This invitation link is invalid or has expired.
          </p>
          <Button onClick={() => navigate('/signin')}>Go to Sign In</Button>
        </div>
      </div>
    );
  }

  // Already used
  if (invitation.status !== 'pending') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <CheckCircle
            aria-hidden="true"
            className="h-12 w-12 text-muted-foreground mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Invitation {invitation.status}
          </h1>
          <p className="text-muted-foreground mb-6">
            This invitation has already been {invitation.status}.
          </p>
          <Button onClick={() => navigate(isAuthenticated ? '/leads' : '/signin')}>
            {isAuthenticated ? 'Go to Dashboard' : 'Go to Sign In'}
          </Button>
        </div>
      </div>
    );
  }

  // Expired
  if (invitation.expiresAt < Date.now()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md p-8">
          <XCircle aria-hidden="true" className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Invitation Expired</h1>
          <p className="text-muted-foreground mb-6">
            This invitation has expired. Ask the organisation owner to send a new one.
          </p>
          <Button onClick={() => navigate('/signin')}>Go to Sign In</Button>
        </div>
      </div>
    );
  }

  // Not authenticated — inline auth form
  if (!isAuthenticated) {
    return (
      <InviteAuthFormWrapper
        email={invitation.email}
        token={token}
        orgName={invitation.orgName}
        onAccepted={() => navigate('/leads', { replace: true })}
      />
    );
  }

  // Authenticated — accept/decline
  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      const result = await acceptInvitation({ token });
      if (result.success) {
        navigate('/leads', { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    try {
      await declineInvitation({ token });
      navigate('/leads', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center max-w-md p-8 bg-card rounded-lg border border-border">
        <h1 className="text-2xl font-bold text-foreground mb-2">Organisation Invitation</h1>
        <p className="text-muted-foreground mb-6">
          You&apos;ve been invited to join <strong>{invitation.orgName}</strong>.
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={handleAccept} disabled={isAccepting || isDeclining}>
            {isAccepting ? (
              <>
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin mr-2" />
                Accepting...
              </>
            ) : (
              'Accept'
            )}
          </Button>
          <Button variant="outline" onClick={handleDecline} disabled={isAccepting || isDeclining}>
            {isDeclining ? (
              <>
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin mr-2" />
                Declining...
              </>
            ) : (
              'Decline'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
